import React, { useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import ClientKeyFormDialog from "./ClientKeyFormDialog";
import {
  capabilitiesKeyField,
  capabilitiesKeyValue,
  capabilityBlocked,
  capabilityMap,
  describeConnection,
  missingScopes,
  reinstallUrl,
} from "../../utils/nodeBuilder";

// Renders whatever the catalog's `builder` block describes:
// - a platform-key notice (SendGrid),
// - Slack connection capabilities, missing scopes and a reinstall link,
// - key management driven by `builder.client_key_form`.

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
  padding: 7px 8px;
  border: 1px solid var(--main-border-color);
  border-radius: 7px;
  background-color: var(--main-bg);
  font-size: 10.5px;
  color: var(--main-font-color);
`;

const Notice = styled.div`
  display: flex;
  gap: 6px;
  align-items: flex-start;
  padding: 7px 8px;
  border: 1px solid var(--main-border-color);
  border-left: 3px solid #3498db;
  border-radius: 7px;
  background-color: var(--main-bg);
  font-size: 10.5px;
  line-height: 1.45;
  color: var(--main-font-color);
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Chip = styled.span`
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(props) => (props.$ok ? "#2ecc71" : "#e74c3c")};
  border: 1px solid ${(props) => (props.$ok ? "#2ecc71" : "#e74c3c")};
  background-color: ${(props) => (props.$ok ? "rgba(46, 204, 113, 0.12)" : "rgba(231, 76, 60, 0.12)")};
`;

const Link = styled.a`
  color: #4dabf7;
  text-decoration: underline;
  font-size: 10.5px;
`;

const SmallButton = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 10.5px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
`;

/**
 * @param {object} props
 * @param {object} props.definition The node definition (its `builder` block drives this).
 * @param {object} props.data The node's flat data.
 * @param {object|null} props.capabilities Capabilities already fetched by the node.
 * @param {boolean} [props.loading]
 * @param {string|null} [props.error]
 */
export const NodeBuilderPanel = ({ definition, data = {}, capabilities = null, loading = false, error = null }) => {
  const builder = definition?.builder ?? {};
  const [showKeys, setShowKeys] = useState(false);

  const hasBuilder = builder && Object.keys(builder).length > 0;
  if (!hasBuilder) return null;

  const keyField = capabilitiesKeyField(builder, definition, data);
  const keyValue = capabilitiesKeyValue(builder, definition, data);

  const connection = describeConnection(capabilities);
  const scopes = missingScopes(capabilities);
  const install = reinstallUrl(capabilities);
  const map = capabilityMap(capabilities);
  const operations = Object.entries(map);

  const sendBlocked = capabilityBlocked(capabilities, ["send_message"]);

  return (
    <>
      {builder.platform_key_required ? (
        <Notice>
          <Icon icon="lucide:info" style={{ flexShrink: 0, marginTop: "1px" }} />
          <span>{builder.platform_key_note ?? "This node uses a platform key configured by the server administrator."}</span>
        </Notice>
      ) : null}

      {builder.capabilities_endpoint ? (
        <Panel className="nodrag">
          {!keyValue ? (
            <span style={{ fontStyle: "italic", color: "#a0a6b1" }}>
              Select a client key to check the connection.
              {keyField ? <span style={{ opacity: 0.7 }}> (field: {keyField})</span> : null}
            </span>
          ) : loading ? (
            <span style={{ fontStyle: "italic", color: "#a0a6b1" }}>Checking connection…</span>
          ) : error ? (
            <span style={{ color: "#e74c3c" }}>{error}</span>
          ) : capabilities ? (
            <>
              <span>
                <Icon icon="lucide:plug" style={{ marginRight: "4px" }} />
                {connection ?? "Connected"}
              </span>

              {operations.length > 0 ? (
                <Chips>
                  {operations.map(([name, ok]) => (
                    <Chip key={name} $ok={ok}>{name}</Chip>
                  ))}
                </Chips>
              ) : null}

              {scopes.length > 0 ? (
                <span style={{ color: "#e74c3c" }}>Missing scopes: {scopes.join(", ")}</span>
              ) : null}

              {install ? (
                <Link href={install} target="_blank" rel="noreferrer">
                  Reinstall the app with the required scopes
                </Link>
              ) : scopes.length > 0 ? (
                <span style={{ fontStyle: "italic", color: "#a0a6b1" }}>
                  Reinstall the app with those scopes, then re-save the tokens.
                </span>
              ) : null}

              {sendBlocked ? (
                <span style={{ color: "#e74c3c" }}>This operation is unavailable for the current connection.</span>
              ) : null}
            </>
          ) : null}
        </Panel>
      ) : null}

      {builder.client_key_form ? (
        <>
          <SmallButton type="button" className="nodrag" onClick={() => setShowKeys(true)}>
            <Icon icon="lucide:key-round" />
            Manage {builder.client_key_form.service ?? "client"} keys
          </SmallButton>
          {showKeys ? (
            <ClientKeyFormDialog form={builder.client_key_form} onClose={() => setShowKeys(false)} />
          ) : null}
        </>
      ) : null}
    </>
  );
};

export default NodeBuilderPanel;
